import React from 'react';
import ReactDOM from 'react-dom';

import { PwcCKEditors } from '@pwc/pwc-deals-rich-text-editor';
import { CommentsAdapter } from '@pwc/pwc-deals-ui';
import { MiniTableContent } from 'features/ingestion/components/MinitableContent';
import {
  isInlineCommentingEnabled,
  isCKEditorHighlightEnabled,
} from '../../helpers/featureFlags';

export const defaults = {
  pluginMiniTableSVG: {
    options: {
      label: 'minitable-SVG-toolbar',
      nameWidget: 'minitable-SVG-toolbar',
      nameCommand: 'insertMiniTableSVGToolbar',
    },
  },
};

const toolbar = {
  items: [
    'heading',
    '|',
    'fontColor',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'subscript',
    'superscript',
    ...(isCKEditorHighlightEnabled ? ['highlight'] : []),
    'bulletedList',
    'numberedList',
    '|',
    'indent',
    'outdent',
    '|',
    'link',
    '|',
    'insertTable',
    '|',
    'addMinitableChart',
  ],
  shouldShowMiniTableText: true,
};

const inlineToolbar = {
  items: [
    'heading',
    '|',
    'fontColor',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'subscript',
    'superscript',
    ...(isCKEditorHighlightEnabled ? ['highlight'] : []),
    'bulletedList',
    'numberedList',
    '|',
    'indent',
    'outdent',
    '|',
    'link',
    '|',
    'insertTable',
    '|',
    'addMinitableChart',
  ],
  shouldShowMiniTableText: true,
};

const inlineCommentingToolbar = {
  items: [
    'heading',
    '|',
    'fontColor',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'subscript',
    'superscript',
    ...(isCKEditorHighlightEnabled ? ['highlight'] : []),
    'bulletedList',
    'numberedList',
    '|',
    'indent',
    'outdent',
    '|',
    'link',
    '|',
    'insertTable',
    '|',
    'inlineComment',
    '|',
    'addMinitableChart',
  ],
  shouldShowMiniTableText: true,
};

const baloonToolbar = {
  items: [
    'fontColor',
    'bold',
    'italic',
    'underline',
    'strikethrough',
    'subscript',
    'superscript',
    ...(isCKEditorHighlightEnabled ? ['highlight'] : []),
    '|',
    'link',
  ],
};

const introConversion = {
  model: 'intro',
  view: {
    name: 'p',
    classes: 'rich-text-editor-intro',
  },
};

const paragraphConversion = {
  model: 'paragraph',
  view: {
    name: 'p',
    classes: 'rich-text-editor-paragraph',
  },
};

const footnotesConversion = {
  model: 'footnotes',
  view: {
    name: 'p',
    classes: 'rich-text-editor-footnotes',
  },
};

const heading1Conversion = {
  model: 'heading1',
  view: {
    name: 'h3',
    classes: 'rich-text-editor-heading3',
  },
};

const heading2Conversion = {
  model: 'heading2',
  view: {
    name: 'h4',
    classes: 'rich-text-editor-heading4',
  },
};

export const customHeadingPlugin = (editor) => {
  editor.conversion.elementToElement(heading1Conversion);
  editor.conversion.elementToElement(heading2Conversion);
  editor.conversion.elementToElement(introConversion);
  editor.conversion.elementToElement(paragraphConversion);
  editor.conversion.elementToElement(footnotesConversion);
};

export const pluginSVGMiniTable = PwcCKEditors.createPluginGenericWidget({
  namePropertyOptions: 'pluginMiniTableSVG',
});

export const pluginMiniTableSVGConfig = {
  options: defaults.pluginMiniTableSVG.options,
  onDelete({ elementComponentWrapper }) {
    ReactDOM.unmountComponentAtNode(elementComponentWrapper);
  },
  render({ elementParent, payload }) {
    if (payload) {
      ReactDOM.render(<MiniTableContent {...payload} />, elementParent);
    }
  },
};

export const customIntroHeadingOnlyPlugin = (editor) => {
  const addIntroClass = (_, data, conversionApi) => {
    conversionApi.writer.addClass(
      'rich-text-editor-intro',
      conversionApi.mapper.toViewElement(data.item)
    );
  };
  editor.conversion.for('downcast').add((dispatcher) => {
    dispatcher.on('insert:listItem', addIntroClass, { priority: 'low' });
    dispatcher.on('insert:paragraph', addIntroClass, { priority: 'low' });
  });
};

const customColorPalette = [
  {
    color: '#d04a02',
    label: 'Orange',
  },
  {
    color: '#ffb600',
    label: 'Yellow',
  },
  {
    color: '#e0301e',
    label: 'Red',
  },
  {
    color: '#eb8c00',
    label: 'Tangerine',
  },
  {
    color: '#d93954',
    label: 'Digital Rose',
  },
  {
    color: '#7d7d7d',
    label: 'Grey',
  },
  {
    color: '#d9d9d9',
    label: 'Light Grey',
  },
];

const editorConfig = {
  language: 'en',
  licenseKey: process.env.REACT_APP_CK_EDITOR_LICENSE,
  toolbar,
  table: {
    contentToolbar: [
      'tableColumn',
      'tableRow',
      'mergeTableCells',
      'tableProperties',
      'tableCellProperties',
      'tableToolbar',
    ],
    tableProperties: {
      borderColors: customColorPalette,
      backgroundColors: customColorPalette,
    },

    tableCellProperties: {
      borderColors: customColorPalette,
      backgroundColors: customColorPalette,
    },
  },
  placeholder: 'Start writing here...',
  extraPlugins: [customHeadingPlugin, pluginSVGMiniTable],
  removePlugins: ['RestrictedEditingMode', 'TextTransformation'],
  ...(isCKEditorHighlightEnabled
    ? {
        highlight: {
          options: [
            {
              model: 'yellowMarker',
              class: 'marker-yellow',
              title: 'Yellow',
              color: '#ffb600',
              type: 'marker',
            },
            {
              model: 'orangeMarker',
              class: 'marker-orange',
              title: 'Orange',
              color: '#bd4301',
              type: 'marker',
            },
            {
              model: 'greyMarker',
              class: 'marker-grey',
              title: 'Grey',
              color: '#bbbbbb',
              type: 'marker',
            },
          ],
        },
      }
    : {}),
  heading: {
    options: [
      {
        ...heading1Conversion,
        title: 'Heading 1',
        class: 'ck-heading-option-heading3',
      },
      {
        ...heading2Conversion,
        title: 'Heading 2',
        class: 'ck-heading-option-heading4',
      },
      {
        ...introConversion,
        title: 'Intro',
        class: 'ck-heading-option-intro',
      },
      {
        ...paragraphConversion,
        title: 'Paragraph',
        class: 'ck-heading-option-paragraph',
      },
      {
        ...footnotesConversion,
        title: 'Footnotes',
        class: 'ck-heading-option-footnotes',
      },
    ],
  },
  fontColor: {
    colors: [
      {
        color: '#aa2417',
        label: 'Red',
      },
      {
        color: '#714300',
        label: 'Orange',
      },
      {
        color: '#4eb523',
        label: 'Green',
      },
      {
        color: '#0060d7',
        label: 'Blue',
      },
      {
        color: '#9013fe',
        label: 'Purple',
      },
    ],
    documentColors: 0,
  },
  pluginMiniTableSVG: pluginMiniTableSVGConfig,
};

export const inlineEditorConfig = {
  ...editorConfig,
  toolbar: inlineToolbar,
  placeholder: `${
    process.env.REACT_APP_CK_EDITOR_INLINE_PLACEHOLDR_PREFIX || ''
  }Start writing here...`,
  list: {
    properties: {
      styles: true,
      startIndex: true,
    },
  },
  comments: {
    editorConfig: {},
  },
};

export const noHeadingEditorConfig = {
  ...editorConfig,
  toolbar: { ...toolbar, items: toolbar.items.slice(2) },
};

export const richTextFreeForm = {
  ...editorConfig,
  toolbar: isInlineCommentingEnabled ? inlineCommentingToolbar : toolbar,
  list: {
    properties: {
      styles: true,
      startIndex: true,
    },
  },
  extraPlugins: [...editorConfig.extraPlugins, CommentsAdapter],
  sidebar: {
    container: document.createElement('div'),
  },
};

export const introHeadingOnlyEditorConfig = {
  ...noHeadingEditorConfig,
  extraPlugins: [customIntroHeadingOnlyPlugin],
};

export const shortEditorConfig = {
  ...editorConfig,
  toolbar: ['bold', '|', 'italic', '|', 'link'],
};

export const shortIntroHeadingOnlyEditorConfig = {
  ...editorConfig,
  extraPlugins: [customIntroHeadingOnlyPlugin],
  toolbar: ['bold', '|', 'italic', '|', 'link'],
  balloonToolbar: { items: ['bold', 'italic', 'link'] },
};

export const shortIntroHeadingOnlyCommentEditorConfig = {
  ...editorConfig,
  extraPlugins: [customIntroHeadingOnlyPlugin, CommentsAdapter],
  toolbar: ['bold', '|', 'italic', '|', 'link', '|', 'inlineComment'],
  balloonToolbar: { items: ['bold', 'italic', 'link', 'inlineComment'] },
  sidebar: {
    container: document.createElement('div'),
  },
};

export const balloonEditorConfig = {
  ...editorConfig,
  placeholder: '',
  toolbar: baloonToolbar,
};

export const readOnlyCKEditorConfig = {
  ...editorConfig,
  placeholder: '',
  toolbar: {
    items: [],
  },
  table: {
    contentToolbar: [],
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
    },
  },
  comments: {
    editorConfig: {},
  },
};

export const getConfigWithCommentsSetup = (
  config,
  adapter,
  sidebarContainer
) => {
  if (adapter) {
    return {
      ...config,
      extraPlugins: [...config.extraPlugins, adapter],
      sidebar: {
        container: sidebarContainer,
      },
    };
  }

  return {
    ...config,
    removePlugins: ['Comments', ...(config.removePlugins || [])],
  };
};

export default editorConfig;
